import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { IDLE_FLAG } from '../components/IdleSignOut'
import { getTheme } from '../lib/theme'

export default function Login() {
  const { signIn, resetPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('signin') // 'signin' | 'reset'
  const [resetSent, setResetSent] = useState(false)
  // Shown once after an automatic sign-out for inactivity
  const [idleNotice] = useState(() => { try { const f = sessionStorage.getItem(IDLE_FLAG); sessionStorage.removeItem(IDLE_FLAG); return !!f } catch (e) { return false } })

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error } = await signIn(email, password)
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      try { localStorage.setItem('inv_last_activity', String(Date.now())) } catch (e) { /* ignore */ }
      navigate('/')
    }
  }

  async function handleReset(e) {
    e.preventDefault()
    setError('')
    if (!email) { setError('Enter your email first.'); return }
    setLoading(true)
    const { error } = await resetPassword(email)
    setLoading(false)
    // Always show the same confirmation (don't reveal whether the email exists).
    if (error && !/rate limit/i.test(error.message)) setError(error.message)
    else setResetSent(true)
  }

  function switchMode(next) {
    setMode(next)
    setError('')
    setResetSent(false)
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: '20px'
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src={getTheme() === 'light' ? '/Decent-Startup_Logo_Black.png' : '/Decent-Startup_Logo_White.png'}
            alt="Decent Startup"
            style={{ width: '160px', height: 'auto', margin: '0 auto 20px', display: 'block' }}
          />
          <h1 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '6px' }}>Inventory Management</h1>
        </div>

        <div className="card">
          {mode === 'reset' ? (
            resetSent ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="alert alert-success">If an account exists for that email, a password reset link is on its way. Check your inbox.</div>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => switchMode('signin')}>
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ color: 'var(--text-2)', fontSize: '13px', lineHeight: 1.5 }}>Enter your email and we'll send you a link to set a new password.</p>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'center' }} onClick={() => switchMode('signin')}>
                  Back to Sign In
                </button>
              </form>
            )
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {idleNotice && <div className="alert alert-warning">You were signed out after 8 hours of inactivity. Please sign in again.</div>}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {error && (
                <div className="alert alert-danger">{error}</div>
              )}
              <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'center' }} onClick={() => switchMode('reset')}>
                Forgot password?
              </button>
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', color: 'var(--text-3)', fontSize: '12px' }}>
          Questions? Contact <a href="mailto:inventory@decentstartup.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>inventory@decentstartup.com</a>
        </p>
      </div>
    </div>
  )
}
